import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { AbstractControl, UntypedFormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'lodash-es';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IInputComponent } from '@nimbus-library/features/shared/components/material/i-input-component';
import { MediaPlayerMode } from '../input-media-player/input-media-player.component';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-input-upload-audiofile',
  templateUrl: './input-upload-audiofile.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputUploadAudiofileComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => InputUploadAudiofileComponent),
    },
  ],
})
export class InputUploadAudiofileComponent extends IInputComponent<File | string> implements OnInit, OnDestroy, Validator {
  @Input()
  public supportedFiles: string[];
  @Input()
  public mimeTypes: string[];
  @Input()
  public mimeTypesNotSupportedPreview: string[];
  @Input()
  public maximumFileSizeMB: number = 20;
  @Input()
  public maximumFileDurationMinutes: number = 10;

  private audio: HTMLAudioElement;
  private fileInput: HTMLInputElement;

  public textControl: UntypedFormControl = new UntypedFormControl();
  public previewNotSupported$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  public get mediaPlayerMode(): MediaPlayerMode {
    return !this.value ? MediaPlayerMode.Preview : MediaPlayerMode.Normal;
  }

  public get isFileInvalid(): boolean {
    return !!this.value && typeof this.value !== 'string' && !!this.ngControl?.invalid;
  }

  public constructor(
    private changeDetectionRef: ChangeDetectorRef,
    private injector: Injector,
    private readonly renderer: Renderer2,
    private readonly translateService: TranslateService,
  ) {
    super(changeDetectionRef, injector, translateService);
  }

  public ngOnInit(): void {
    super.ngOnInit();

    this.audio = new Audio();
    this.audio.onloadeddata = (): void => {
      this.ngControl.control.updateValueAndValidity();
    };

    this.fileInput = <HTMLInputElement>this.renderer.createElement('input');
    this.fileInput.setAttribute('type', 'file');
    this.fileInput.setAttribute('accept', this.supportedFiles.join(','));
    fromEvent(this.fileInput, 'change')
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.textControl.setValue(null);

        if (this.ngControl) {
          this.ngControl.control.reset();
        }

        this.previewNotSupported$.next(false);
        const file: File = first(this.fileInput?.files ?? []);
        if (!file) {
          return;
        }
        this.audio.src = window.URL.createObjectURL(file);
        if (this.mimeTypesNotSupportedPreview.includes(file.type)) {
          this.previewNotSupported$.next(true);
        }
        if (this.ngControl) {
          this.ngControl.control.markAsTouched();
          this.ngControl.control.markAsDirty();
          this.ngControl.control.setValue(file);
        }
        this.textControl.setValue(file.name);
        this.changeDetectionRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.audio?.remove();
  }

  public writeValue(value: File | string): void {
    super.writeValue(value);
    if (value && typeof value === 'string') {
      this.textControl?.setValue(value.split('/').pop());
    }
  }

  public validate(control: AbstractControl): { [key: string]: any } {
    let result = null;

    if (control && control.value) {
      if (typeof control.value === 'string') {
        return null;
      }

      if (!this.mimeTypes.some((type) => control.value.type === type)) {
        result = { invalidFileType: this.supportedFiles };
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      } else if (control.value.size / 1024 / 1024 > this.maximumFileSizeMB) {
        result = { invalidFileSize: this.maximumFileSizeMB };
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      } else if (this.audio && this.audio.duration / 60 > this.maximumFileDurationMinutes) {
        result = { invalidFileDuration: this.maximumFileDurationMinutes };
      }
    }
    return result;
  }

  public openFileDialog(): void {
    this.fileInput.click();
  }
}
